import React from 'react';
import { Tooltip } from 'antd';
import { ExclamationCircleFilled, WarningFilled } from '@ant-design/icons';
import colors from '../constants/Colors';
import TagRender from '../common/TagRender';
import sortByString, { sortByLocalCompare, isNullOrUndefined } from '../helpers/helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import EquipmentListClock from './EquipmentListClock';

export const didExceedHoursWarning = ({
  nearThresholdHours,
  thresholdHours,
  currentHours,
}) => {
  let realNearThreshHours = nearThresholdHours;
  if (isNullOrUndefined(nearThresholdHours) && !isNullOrUndefined(thresholdHours)) {
    realNearThreshHours = Math.max(0, thresholdHours - 50);
  }

  return (
    thresholdHours
    && !isNullOrUndefined(realNearThreshHours)
    && currentHours > (realNearThreshHours * 60 * 60 * 1000)
  );
};

export const didExceedMileageWarning = ({
  nearThresholdMileage,
  thresholdMileage,
  currentMileage,
}) => {
  let realNearThreshMileage = nearThresholdMileage;
  if (isNullOrUndefined(nearThresholdMileage) && !isNullOrUndefined(thresholdMileage)) {
    realNearThreshMileage = Math.max(0, thresholdMileage - 50);
  }
  return (
    thresholdMileage
    && !isNullOrUndefined(realNearThreshMileage)
    && currentMileage > realNearThreshMileage
  );
};

export const getDisplayColumns = ({
  divisions,
  statuses,
  equipmentTypeMap,
  useRedesign,
  onTimerClick,
  labelMap,
  vendorMap,
}) => {
  const cols = [
    {
      title: 'ID',
      dataIndex: 'code',
      width: 50,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByString('code'),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByLocalCompare('name'),
    }];

  cols.push({
    title: '',
    dataIndex: 'thresholdWarning',
    width: 50,
    render: (_, record) => {
      const {
        nearThresholdHours,
        thresholdHours,
        currentHours,
        nearThresholdMileage,
        thresholdMileage,
        currentMileage,
        thresholdEmailSent,
        thresholdMileageEmailSent,
      } = record;
      const exceedHourWarn = didExceedHoursWarning({
        nearThresholdHours,
        thresholdHours,
        currentHours,
      });
      const exceedMileageWarn = didExceedMileageWarning({
        nearThresholdMileage,
        thresholdMileage,
        currentMileage,
      });
      const sentEmail = thresholdEmailSent || thresholdMileageEmailSent;
      const showWarn = (
        !sentEmail
        && (exceedHourWarn || exceedMileageWarn)
      );

      if (!showWarn && !sentEmail) return null;
      if (sentEmail) {
        return (
          <Tooltip
            title="Threshold Exceeded."
            color="white"
            overlayInnerStyle={{
              color: 'black',
            }}
          >
            <ExclamationCircleFilled
              style={{
                fontSize: 16,
                color: colors.ONTRACCR_RED,
              }}
            />
          </Tooltip>
        )
      }
      return (
        <Tooltip
          title="Equipment is nearing threshold."
          color="white"
          overlayInnerStyle={{
            color: 'black',
          }}
        >
          <WarningFilled
            style={{
              fontSize: 16,
              color: colors.YELLOW_500,
            }}
          />
        </Tooltip>
      )
    }
  })

  cols.push(
    {
      title: 'Clock',
      dataIndex: 'clock',
      width: 200,
      render: (_, record) => (
        <EquipmentListClock
          {...record}
          onTimerClick={onTimerClick}
        />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'equipmentTypeId',
      width: 150,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (equipmentTypeId) => (
        equipmentTypeMap[equipmentTypeId]?.name ?? 'None'
      ),
      sorter: (a, b) => {
        const aVal = (equipmentTypeMap[a.equipmentTypeId]?.name ?? 'None')?.toLowerCase()?.trim();
        const bVal = (equipmentTypeMap[b.equipmentTypeId]?.name ?? 'None')?.toLowerCase()?.trim();
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
      },
    },
    {
      title: 'Status',
      dataIndex: 'statusId',
      width: 100,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (statusId) => (
        statuses[statusId]?.status
      ),
      sorter: (a, b) => {
        const aVal = (statuses[a.statusId]?.status || '~')?.toLowerCase()?.trim();
        const bVal = (statuses[b.statusId]?.status || '~')?.toLowerCase()?.trim();
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
      },
    });

  if (Object.keys(labelMap).length > 0) {
    cols.push({
      title: 'Labels',
      dataIndex: 'labels',
      width: 125,
      render: (labels) => (
        <TagRender
          tagMap={labelMap}
          tagIds={labels?.map((label) => label.id)}
          useRedesign={useRedesign}
          labelKey="title"
        />
      ),
    });
  }

  cols.push({
    title: 'Vendor',
    dataIndex: 'vendorId',
    width: 150,
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
    render: (vendorId) => (vendorMap[vendorId]?.name || ''),
    sorter: (a, b) => {
      const aVal = (vendorMap[a.vendorId]?.name || '')?.toLowerCase()?.trim();
      const bVal = (vendorMap[b.vendorId]?.name || '')?.toLowerCase()?.trim();
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    },
  });

  if (Object.keys(divisions).length === 1) return cols;
  return cols.concat([{
    title: 'Divisions',
    dataIndex: 'divisionIds',
    width: 150,
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
    render: (divisionIds) => (
      <TagRender
        tagMap={divisions}
        tagIds={divisionIds}
        useRedesign={useRedesign}
      />
    ),
  }]);
};

export const formatMillisecondsToHHMM = (ms) => {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const padded = (num) => num.toString().padStart(2, '0');
  return `${padded(hours)}:${padded(minutes)}`;
};

export const formatEquipmentLabelFromCompanySettings = ({ name, code, settings = {} }) => {
  const { equipmentLabelFormat = 'both' } = settings;

  switch (equipmentLabelFormat) {
    case 'id': {
      if (!code) break;
      return code;
    }
    case 'both': {
      if (!code) break;
      return `${code} - ${name}`;
    }
    default: {
      return name;
    }
  }
  return name;
};

export const formatEquipmentLabelsFromDropdownValues = ({
  values = [],
  equipmentIdMap = {},
  settings = {},
}) => {
  const formattedValues = values.map(({ id }) => {
    const {
      [id]: {
        name,
        code,
      } = {},
    } = equipmentIdMap;

    return formatEquipmentLabelFromCompanySettings({ name, code, settings });
  });

  return formattedValues;
};

export const convertEquipmentToRow = ({
  equipment,
  divisions,
  equipmentTypeMap,
  equipmentProjects,
  projectMap,
  userMap,
  costcodeMap,
  vendorMap,
  settings,
}) => {
  const {
    code,
    name,
    divisionIds,
    equipmentTypeId,
    status,
    labels,
    userId,
    costcodeId,
    vendorId,
    hourlyCost,
    hourlyBillingRate,
    dailyCost,
    dailyBillingRate,
    weeklyCost,
    weeklyBillingRate,
    monthlyCost,
    monthlyBillingRate,
    qrCode,
  } = equipment;

  const projectIds = equipmentProjects?.[equipment.id];
  const projectNames = projectIds?.map((projId) => formatProjectLabelFromCompanySettings({
    name: projectMap?.[projId]?.name,
    number: projectMap?.[projId]?.number,
    settings,
  })).filter((projName) => !!projName).join(', ');

  const divisionNames = divisionIds.map((divId) => divisions?.[divId]?.name)
    .filter((divName) => !!divName)
    .join(', ');

  const labelNames = labels.map((label) => label?.title)
    .join(', ');

  const userName = userMap?.[userId]?.name;
  const costCodeName = costcodeMap?.[costcodeId]?.name;
  const vendorName = vendorMap?.[vendorId]?.name;

  return {
    ID: code,
    Name: name,
    Type: equipmentTypeMap[equipmentTypeId]?.name ?? 'None',
    Status: status,
    Divisions: divisionNames,
    Projects: projectNames,
    Labels: labelNames,
    'Assigned User': userName,
    'Cost Code': costCodeName,
    Vendor: vendorName,
    'Hourly Cost': hourlyCost,
    'Hourly Billing Rate': hourlyBillingRate,
    'Daily Cost': dailyCost,
    'Daily Billing Rate': dailyBillingRate,
    'Weekly Cost': weeklyCost,
    'Weekly Billing Rate': weeklyBillingRate,
    'Monthly Cost': monthlyCost,
    'Monthly Billing Rate': monthlyBillingRate,
    'QR Code Value': qrCode?.value,
  };
};

export const bold = (text) => (
  <span style={{ fontFamily: 'roboto-regular' }}>
    {text}
    {' '}
  </span>
);

export function FormButton({ formName, formId, onFormClick }) {
  return (
    <button
      type="button"
      onClick={() => onFormClick(formId)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        color: 'blue',
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      {bold(formName)}
    </button>
  );
}

export const getEquipmentUpdateText = ({
  updateType,
  value,
  formName,
  formId,
  defaultedUser,
  onFormClick,
  unit,
}) => {
  const formButton = <FormButton formName={formName} formId={formId} onFormClick={onFormClick} />;

  return (
    <span>
      {bold(defaultedUser)} submitted the form which {formButton} triggered an
      update equipment workflow step that {updateType === 'Add' ? ' added ' : ' overridden '} {bold(value)} {unit}
    </span>
  );
};
